import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  MarketCapRecord,
  MarketCapTimeframe,
  useCompanyMarketCap,
} from "../hooks/useCompanyMarketCap";
import { useStockCurrency } from "../../../../contexts/StockCurrencyContext";
import { getChartColors } from "../constants/chartColors";
import { formatNumber } from "../utils/formatNumber";
import GenericFinancialLineChart from "./shared/GenericFinancialLineChart";
import ChartTooltip from "./shared/ChartTooltip";
import { evenlySpacedTickValues } from "../utils/evenlySpacedTickValues";

interface MarketCapChartProps {
  ticker: string;
  height?: number;
  isModal?: boolean;
  onCloseModal?: () => void;
  renderCard?: boolean;
  showFullscreen?: boolean;
  noHover?: boolean;
}

// x is the ISO date so every point gets its own slot on the point scale;
// the axis formats it per timeframe.
interface MarketCapPoint {
  x: string;
  y: number;
}

const TIMEFRAME_OPTIONS = [
  { value: "1M", label: "Monthly" },
  { value: "1Y", label: "Yearly" },
];

// One point per week keeps a year readable; a month shows every session.
const sampleRecords = (
  records: MarketCapRecord[],
  timeframe: MarketCapTimeframe,
): MarketCapRecord[] => {
  const sorted = [...records].sort(
    (a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf(),
  );
  if (timeframe === "1M") return sorted;
  const weeks = new Map<string, MarketCapRecord>();
  sorted.forEach((record) => {
    weeks.set(dayjs(record.date).startOf("week").format("YYYY-MM-DD"), record);
  });
  return Array.from(weeks.values());
};

const tickLabelFormat = (timeframe: MarketCapTimeframe) =>
  timeframe === "1Y" ? "MMM ’YY" : "MMM D";

const MarketCapChart: React.FC<MarketCapChartProps> = ({
  ticker,
  height = 400,
  isModal = false,
  onCloseModal,
  renderCard = true,
  showFullscreen = false,
  noHover = false,
}) => {
  const theme = useTheme();
  // Same breakpoint the price and volume charts use to size their label count.
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { currencySymbol } = useStockCurrency();
  const colors = getChartColors(theme.palette.mode === "dark");
  const [timeframe, setTimeframe] = useState<MarketCapTimeframe>("1Y");
  const { data, isLoading, error } = useCompanyMarketCap(ticker, timeframe);

  const { points, tickDates, areaBaseline } = useMemo(() => {
    const sampled = sampleRecords(data ?? [], timeframe).filter((record) =>
      Number.isFinite(record.marketCap),
    );
    const points = sampled.map(
      (record): MarketCapPoint => ({ x: record.date, y: record.marketCap }),
    );
    const values = points.map((point) => point.y);
    const min = values.length > 0 ? Math.min(...values) : 0;
    const max = values.length > 0 ? Math.max(...values) : 0;
    // Start the fill a little under the lowest point so the line never sits on
    // the floor of its own area.
    const areaBaseline = max > min ? min - (max - min) * 0.08 : min * 0.98;
    // Equal index spacing is the only way to keep labels evenly spaced on a
    // point scale, whatever the calendar does (see evenlySpacedTickValues).
    return {
      points,
      tickDates: evenlySpacedTickValues(
        points.map((point) => point.x),
        isMobile,
      ),
      areaBaseline,
    };
  }, [data, timeframe, isMobile]);

  const first = points[0];
  const money = (value: number, decimals = 1) =>
    `${currencySymbol}${formatNumber(value, decimals)}`;

  return (
    <GenericFinancialLineChart
      title="Market Cap"
      ticker={ticker}
      height={height}
      isModal={isModal}
      renderCard={renderCard}
      showFullscreen={showFullscreen}
      noHover={noHover}
      onCloseModal={onCloseModal}
      isLoading={isLoading}
      error={error}
      headerToggleOptions={TIMEFRAME_OPTIONS}
      headerToggleValue={timeframe}
      onHeaderToggleChange={(value) =>
        setTimeframe(value as MarketCapTimeframe)
      }
      headerTitleAlign="center"
      headerTitleColor="text.primary"
      chartData={[{ id: "Market cap", data: points }]}
      isEmpty={points.length === 0}
      noDataDescription="No market cap history is available for this period."
      yAxisLegend="Market cap"
      yAxisFormat={(value) => money(value)}
      xAxisTickRotation={0}
      axisBottomTickValues={tickDates}
      axisBottomFormat={(value) =>
        dayjs(value).format(tickLabelFormat(timeframe))
      }
      axisBottomTickPadding={12}
      rightMargin={isMobile ? 30 : 40}
      colors={() => colors.positive}
      lineWidth={2.5}
      pointSize={0}
      curve="monotoneX"
      enableArea
      areaOpacity={0.4}
      areaBaselineValue={areaBaseline}
      enableGridX
      useSlices
      defs={[
        {
          id: "marketCapGradient",
          type: "linearGradient",
          colors: [
            { offset: 0, color: colors.positive, opacity: 0.65 },
            { offset: 55, color: colors.positive, opacity: 0.25 },
            { offset: 100, color: colors.positive, opacity: 0 },
          ],
        },
      ]}
      fill={[{ match: "*", id: "marketCapGradient" }]}
      tooltip={(point) => {
        const datum = point.data as MarketCapPoint;
        const rows = [{ label: "Market cap", value: money(datum.y, 2) }];
        if (first && first.x !== datum.x && first.y > 0) {
          const change = ((datum.y - first.y) / first.y) * 100;
          rows.push({
            label: `Since ${dayjs(first.x).format("MMM D, YYYY")}`,
            value: `${change >= 0 ? "+" : ""}${change.toFixed(1)}%`,
          });
        }
        return (
          <ChartTooltip
            header={dayjs(datum.x).format("MMM D, YYYY")}
            headerColor={colors.positive}
            rows={rows}
          />
        );
      }}
    />
  );
};

export default React.memo(MarketCapChart);
